/**
 * Create Group Dialog Component
 * Comprehensive dialog for creating new groups with tag selection
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Slider,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@mui/material'
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import { ImageUploadZone } from '@/components/common/ImageUploadZone'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useCachedTags } from '@/hooks/useCachedTags'
import { TagType } from '@/features/adventures/types/adventure-editor.types'
import { socialApi } from '@api/endpoints/social'
import { ApiService } from '@api/apiService'
import type { CreateGroupRequest } from '@api/types/social'
import { GroupVisibility, getGroupVisibilityName, getGroupVisibilityDescription } from '@api/types/social'

interface CreateGroupDialogProps {
  open: boolean
  onClose: () => void
}

export function CreateGroupDialog({ open, onClose }: CreateGroupDialogProps) {
  const queryClient = useQueryClient()
  const { getCategoriesByType, isLoading: isLoadingTags } = useCachedTags()

  // Form state
  const [groupName, setGroupName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<GroupVisibility>(GroupVisibility.Public)
  const [maxMembers, setMaxMembers] = useState(50)
  const [groupImage, setGroupImage] = useState<string | null>(null)
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null)
  const [expirationDate, setExpirationDate] = useState<Date | null>(null)
  const [selectedInterestTagIds, setSelectedInterestTagIds] = useState<string[]>([])
  const [selectedLookingForTagIds, setSelectedLookingForTagIds] = useState<string[]>([])
  const [selectedAdventureTypeTagIds, setSelectedAdventureTypeTagIds] = useState<string[]>([])

  // UI state
  const [expandedInterestCategory, setExpandedInterestCategory] = useState<string | null>(null)
  const [expandedLookingForCategory, setExpandedLookingForCategory] = useState<string | null>(null)
  const [expandedAdventureTypeCategory, setExpandedAdventureTypeCategory] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Get tag categories
  const interestCategories = getCategoriesByType(TagType.Interests)
  const lookingForCategories = getCategoriesByType(TagType.LookingFor)
  const adventureTypeCategories = getCategoriesByType(TagType.AdventureTypes)

  // Get all tags from categories
  const interestTags = interestCategories.flatMap(cat => cat.tags || [])
  const lookingForTags = lookingForCategories.flatMap(cat => cat.tags || [])
  const adventureTypeTags = adventureTypeCategories.flatMap(cat => cat.tags || [])

  // Create group mutation
  const createGroupMutation = useMutation({
    mutationFn: async (request: CreateGroupRequest) => {
      // Create group without image first
      const group = await socialApi.createGroup(request)

      // If there's an image file to upload, upload it after creation
      if (uploadedImageFile && group.id) {
        const formData = new FormData()
        formData.append('file', uploadedImageFile)
        await ApiService.upload(`/api/groups/${group.id}/image`, formData)
      }

      return group
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      handleClose()
    },
  })

  // Handle image upload (store file for later upload after group creation)
  const handleImageSelect = (file: File) => {
    // Create preview URL
    const reader = new FileReader()
    reader.onloadend = () => {
      setGroupImage(reader.result as string)
      setUploadedImageFile(file)
      setErrors(prev => ({ ...prev, groupImage: '' }))
    }
    reader.readAsDataURL(file)
  }

  // Tag toggle handlers
  const handleToggleInterestTag = (tagId: string) => {
    setSelectedInterestTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  const handleToggleLookingForTag = (tagId: string) => {
    setSelectedLookingForTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  const handleToggleAdventureTypeTag = (tagId: string) => {
    setSelectedAdventureTypeTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!groupName.trim()) {
      newErrors.groupName = 'Group name is required'
    } else if (groupName.length < 3) {
      newErrors.groupName = 'Group name must be at least 3 characters'
    } else if (groupName.length > 100) {
      newErrors.groupName = 'Group name must be less than 100 characters'
    }

    if (description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters'
    }

    if (expirationDate && expirationDate <= new Date()) {
      newErrors.expirationDate = 'Expiration date must be in the future'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle submit
  const handleSubmit = () => {
    if (!validate()) return

    const request: CreateGroupRequest = {
      name: groupName.trim(),
      description: description.trim() || undefined,
      visibility,
      maxMembers: maxMembers > 0 ? maxMembers : undefined,
      // Don't include groupImage - it will be uploaded after group creation
      interestTagIds: selectedInterestTagIds.length > 0 ? selectedInterestTagIds : undefined,
      lookingForTagIds: selectedLookingForTagIds.length > 0 ? selectedLookingForTagIds : undefined,
      adventureTypeTagIds: selectedAdventureTypeTagIds.length > 0 ? selectedAdventureTypeTagIds : undefined,
      expirationDate: expirationDate ? expirationDate.toISOString() : undefined,
    }

    createGroupMutation.mutate(request)
  }

  // Handle close
  const handleClose = () => {
    // Reset form
    setGroupName('')
    setDescription('')
    setVisibility(GroupVisibility.Public)
    setMaxMembers(50)
    setGroupImage(null)
    setUploadedImageFile(null)
    setExpirationDate(null)
    setSelectedInterestTagIds([])
    setSelectedLookingForTagIds([])
    setSelectedAdventureTypeTagIds([])
    setErrors({})
    onClose()
  }

  // Get chip style
  const getTagChipStyle = (categoryColor?: string, isSelected?: boolean) => {
    if (isSelected) {
      return {
        backgroundColor: categoryColor || '#1976d2',
        color: 'white',
        borderColor: categoryColor || '#1976d2',
      }
    }
    return {
      borderColor: categoryColor || '#ccc',
      color: categoryColor || '#666',
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={false}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Create New Group</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Error Alert */}
          {createGroupMutation.isError && (
            <Alert severity="error">
              {createGroupMutation.error instanceof Error
                ? createGroupMutation.error.message
                : 'Failed to create group'}
            </Alert>
          )}

          {/* Basic Info Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Group Name"
                required
                fullWidth
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                error={!!errors.groupName}
                helperText={errors.groupName || `${groupName.length}/100`}
                inputProps={{ maxLength: 100 }}
              />

              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                error={!!errors.description}
                helperText={errors.description || `${description.length}/500`}
                inputProps={{ maxLength: 500 }}
              />

              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                  Group Visibility
                </FormLabel>
                <RadioGroup
                  value={visibility}
                  onChange={(e) => setVisibility(Number(e.target.value) as GroupVisibility)}
                >
                  {[GroupVisibility.Public, GroupVisibility.MatchingTagRequired, GroupVisibility.InviteOnly].map((vis) => (
                    <Box
                      key={vis}
                      sx={{
                        mb: 1,
                        p: 2,
                        border: '1px solid',
                        borderColor: visibility === vis ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        backgroundColor: visibility === vis ? 'action.selected' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={() => setVisibility(vis)}
                    >
                      <FormControlLabel
                        value={vis}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: visibility === vis ? 600 : 400 }}>
                              {getGroupVisibilityName(vis)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getGroupVisibilityDescription(vis)}
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0, width: '100%' }}
                      />
                    </Box>
                  ))}
                </RadioGroup>
              </FormControl>

              <Box>
                <Typography variant="body2" gutterBottom>
                  Max Members: {maxMembers}
                </Typography>
                <Slider
                  value={maxMembers}
                  onChange={(_, value) => setMaxMembers(value as number)}
                  min={1}
                  max={500}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 100, label: '100' },
                    { value: 250, label: '250' },
                    { value: 500, label: '500' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            </Box>
          </Box>

          {/* Image Upload Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Group Avatar
            </Typography>
            <ImageUploadZone
              value={groupImage}
              onFileSelect={handleImageSelect}
              onClear={() => {
                setGroupImage(null)
                setUploadedImageFile(null)
                setErrors(prev => ({ ...prev, groupImage: '' }))
              }}
              maxSizeMB={5}
              label="Upload Group Avatar"
              description="Drag and drop, paste, or click to select"
              isUploading={createGroupMutation.isPending}
              error={errors.groupImage}
              allowCamera={true}
              disabled={createGroupMutation.isPending}
              aspectRatio="1:1"
              cameraMode="user"
            />
          </Box>

          {/* Expiration Date Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Expiration Date (Optional)
            </Typography>
            <Box sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Group Expiration Date"
                  value={expirationDate}
                  onChange={(newValue) => setExpirationDate(newValue)}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.expirationDate,
                      helperText: errors.expirationDate || 'When should this group automatically disband?',
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          {/* Tags Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Tags & Interests
            </Typography>

            {/* Selected Tags Summary */}
            {(selectedInterestTagIds.length > 0 ||
              selectedLookingForTagIds.length > 0 ||
              selectedAdventureTypeTagIds.length > 0) && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, mt: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Selected Tags ({selectedInterestTagIds.length + selectedLookingForTagIds.length + selectedAdventureTypeTagIds.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {selectedInterestTagIds.map((tagId) => {
                    const tag = interestTags.find(t => t.id === tagId)
                    if (!tag) return null
                    const category = interestCategories.find(c => c.tags?.some(t => t.id === tagId))
                    return (
                      <Chip
                        key={tagId}
                        label={tag.displayName}
                        size="small"
                        onDelete={() => handleToggleInterestTag(tagId)}
                        sx={{ backgroundColor: category?.color, color: 'white' }}
                      />
                    )
                  })}
                  {selectedLookingForTagIds.map((tagId) => {
                    const tag = lookingForTags.find(t => t.id === tagId)
                    if (!tag) return null
                    const category = lookingForCategories.find(c => c.tags?.some(t => t.id === tagId))
                    return (
                      <Chip
                        key={tagId}
                        label={tag.displayName}
                        size="small"
                        onDelete={() => handleToggleLookingForTag(tagId)}
                        sx={{ backgroundColor: category?.color, color: 'white' }}
                      />
                    )
                  })}
                  {selectedAdventureTypeTagIds.map((tagId) => {
                    const tag = adventureTypeTags.find(t => t.id === tagId)
                    if (!tag) return null
                    const category = adventureTypeCategories.find(c => c.tags?.some(t => t.id === tagId))
                    return (
                      <Chip
                        key={tagId}
                        label={tag.displayName}
                        size="small"
                        onDelete={() => handleToggleAdventureTypeTag(tagId)}
                        sx={{ backgroundColor: category?.color, color: 'white' }}
                      />
                    )
                  })}
                </Box>
              </Paper>
            )}

            {/* Interest Tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Interest Tags
              </Typography>
              {interestCategories.map((category) => (
                <Accordion
                  key={category.id}
                  expanded={expandedInterestCategory === category.id}
                  onChange={(_, isExpanded) =>
                    setExpandedInterestCategory(isExpanded ? category.id : null)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography sx={{ flexGrow: 1 }}>
                        {category.icon} {category.displayName}
                      </Typography>
                      <Chip
                        label={selectedInterestTagIds.filter(id =>
                          category.tags?.some(t => t.id === id)
                        ).length}
                        size="small"
                        sx={{ backgroundColor: category.color, color: 'white' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {category.tags?.filter(t => t.isActive).map((tag) => {
                        const isSelected = selectedInterestTagIds.includes(tag.id)
                        return (
                          <Chip
                            key={tag.id}
                            label={
                              <>
                                {tag.icon && <span style={{ marginRight: 4 }}>{tag.icon}</span>}
                                {tag.displayName}
                              </>
                            }
                            onClick={() => handleToggleInterestTag(tag.id)}
                            variant={isSelected ? 'filled' : 'outlined'}
                            size="small"
                            sx={getTagChipStyle(category.color, isSelected)}
                            title={tag.description}
                          />
                        )
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {/* Looking For Tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Looking For Tags
              </Typography>
              {lookingForCategories.map((category) => (
                <Accordion
                  key={category.id}
                  expanded={expandedLookingForCategory === category.id}
                  onChange={(_, isExpanded) =>
                    setExpandedLookingForCategory(isExpanded ? category.id : null)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography sx={{ flexGrow: 1 }}>
                        {category.icon} {category.displayName}
                      </Typography>
                      <Chip
                        label={selectedLookingForTagIds.filter(id =>
                          category.tags?.some(t => t.id === id)
                        ).length}
                        size="small"
                        sx={{ backgroundColor: category.color, color: 'white' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {category.tags?.filter(t => t.isActive).map((tag) => {
                        const isSelected = selectedLookingForTagIds.includes(tag.id)
                        return (
                          <Chip
                            key={tag.id}
                            label={
                              <>
                                {tag.icon && <span style={{ marginRight: 4 }}>{tag.icon}</span>}
                                {tag.displayName}
                              </>
                            }
                            onClick={() => handleToggleLookingForTag(tag.id)}
                            variant={isSelected ? 'filled' : 'outlined'}
                            size="small"
                            sx={getTagChipStyle(category.color, isSelected)}
                            title={tag.description}
                          />
                        )
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {/* Adventure Type Tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Adventure Type Tags
              </Typography>
              {adventureTypeCategories.map((category) => (
                <Accordion
                  key={category.id}
                  expanded={expandedAdventureTypeCategory === category.id}
                  onChange={(_, isExpanded) =>
                    setExpandedAdventureTypeCategory(isExpanded ? category.id : null)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography sx={{ flexGrow: 1 }}>
                        {category.icon} {category.displayName}
                      </Typography>
                      <Chip
                        label={selectedAdventureTypeTagIds.filter(id =>
                          category.tags?.some(t => t.id === id)
                        ).length}
                        size="small"
                        sx={{ backgroundColor: category.color, color: 'white' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {category.tags?.filter(t => t.isActive).map((tag) => {
                        const isSelected = selectedAdventureTypeTagIds.includes(tag.id)
                        return (
                          <Chip
                            key={tag.id}
                            label={
                              <>
                                {tag.icon && <span style={{ marginRight: 4 }}>{tag.icon}</span>}
                                {tag.displayName}
                              </>
                            }
                            onClick={() => handleToggleAdventureTypeTag(tag.id)}
                            variant={isSelected ? 'filled' : 'outlined'}
                            size="small"
                            sx={getTagChipStyle(category.color, isSelected)}
                            title={tag.description}
                          />
                        )
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={createGroupMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createGroupMutation.isPending || isLoadingTags}
        >
          {createGroupMutation.isPending ? 'Creating...' : 'Create Group'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
